import {useEffect, useMemo, useState} from 'react';
import Button from '@/shared/components/Button';
import {Box, Typography} from '@mui/material';
import {CustomizationStyle} from './Customization.style';
import {Field, Form, Formik} from 'formik';
import Input from '@/shared/components/Inputs/Input';
import ColorPicker from '@/shared/components/ColorPicker';
import {ReactComponent as Instagram} from '@/assets/icons/instagram.svg';
import {ReactComponent as Facebook} from '@/assets/icons/facebook.svg';
import {ReactComponent as YouTube} from '@/assets/icons/youtube.svg';
import {ReactComponent as Twitter} from '@/assets/icons/twiter.svg';
import {ReactComponent as AlignLeft} from '@/assets/icons/align-left.svg';
import {ReactComponent as AlignRight} from '@/assets/icons/align-right.svg';
import {ReactComponent as AlignCenter} from '@/assets/icons/align-vertically.svg';
import * as Yup from 'yup';
import axios from 'axios';
import {CommunicationService} from '@/utils/services/organization-communication.services';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {
  createEmailTemplate,
  getEmailCustomizationTemplateSelector,
  getEmailTemplate,
  organizationCommunicationLoaderSelector,
} from '@/store/organization-communication';
import Loader from '@/shared/components/Loader';
import {formContentStyle} from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting/FormContent/FormContent.styles';
import {getCurrentEventSelector} from '@/store/organization-event';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {position} from '@/utils/constants/communication';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
import {isFile} from '@/utils/constants';

const validationSchema = Yup.object().shape({
  fromName: Yup.string().required('Required'),
  replyTo: Yup.string().required('Required'),
  bodyBackground: Yup.string().required('Required'),
  headerColor: Yup.string().required('Required'),
  logoPosition: Yup.string().required('Required'),
  webpageLogo: Yup.mixed<File | string>().required('Required'),
  optional1: Yup.string().url('Invalid URL'),
  optional2: Yup.string().url('Invalid URL'),
  optional3: Yup.string().url('Invalid URL'),
  optional4: Yup.string().url('Invalid URL'),
  logoHeight: Yup.string().required('Required'),
  primaryColor: Yup.string().required('Required'),
  eventLogoOrImage: Yup.mixed<File | string>().required('Required'),
  textColor: Yup.string().required('Required'),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const sendPresignedImage = async (presignedImgUrl: any, file: any) => {
  await axios.put(presignedImgUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};
export const getImage = async (logo: File) => {
  const presignedLogoUrl = await CommunicationService.presignedImage();

  await sendPresignedImage(presignedLogoUrl.data.url, logo);

  return presignedLogoUrl.data.key.split('/').pop();
};
const positionToFlex = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
} as const;

const getSaveCustomizationData = (values: FormValues, logo: string, eventLogo: string, eventId: number) => {
  const position = {
    start: 3,
    center: 2,
    end: 1,
  };

  return {
    eventId,
    fromName: values.fromName,
    replyTo: values.replyTo,
    primaryColor: values.primaryColor,
    bodyBackground: values.bodyBackground,
    headerColor: values.headerColor,
    logo,
    logoHeight: Number(values.logoHeight),
    logoPosition: position[values.logoPosition as keyof typeof position],
    eventLogo,
    textColor: values.textColor,
    socialLinks: [
      {
        id: 1,
        emailCustomizationId: 555,
        eventId,
        facebookUrl: values.optional1,
        instagramUrl: values.optional2,
        twitterUrl: values.optional3,
        youTubeUrl: values.optional4,
      },
    ],
  };
};

const CommunicationCustomization = () => {
  const dispatch = useAppDispatch();

  const eventId = useAppSelector(getCurrentEventSelector)?.id;

  const communicationTemplate = useAppSelector(getEmailCustomizationTemplateSelector);

  const communicationTemplateLoading = useAppSelector(organizationCommunicationLoaderSelector);

  const [isLoading, setIsLoading] = useState(false);

  const initialValues = useMemo(() => {
    const logoPositionKey = (communicationTemplate?.logoPosition ?? 3) as keyof typeof position;
    return {
      fromName: communicationTemplate?.fromName || '',
      replyTo: communicationTemplate?.replyTo || '',
      bodyBackground: communicationTemplate?.bodyBackground || '#FFFFFF',
      headerColor: communicationTemplate?.headerColor || '#FFFFFF',
      logoPosition: position[logoPositionKey] || 'start',
      webpageLogo: communicationTemplate?.logo || '',
      optional1: communicationTemplate?.socialLinks?.[0]?.facebookUrl || '',
      optional2: communicationTemplate?.socialLinks?.[0]?.instagramUrl || '',
      optional3: communicationTemplate?.socialLinks?.[0]?.twitterUrl || '',
      optional4: communicationTemplate?.socialLinks?.[0]?.youTubeUrl || '',
      textColor: communicationTemplate?.textColor || '#000000',
      logoHeight: communicationTemplate?.logoHeight?.toString() || '',
      primaryColor: communicationTemplate?.primaryColor || '#26b5ed',
      eventLogoOrImage: communicationTemplate?.eventLogo || '',
    };
  }, [eventId, communicationTemplate]);

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);

    let logo: string = '';
    if (typeof values.webpageLogo === 'string') {
      logo = values.webpageLogo;
    } else if (values.webpageLogo instanceof File) {
      logo = await getImage(values.webpageLogo);
    }

    let eventLogo: string = '';
    if (typeof values.eventLogoOrImage === 'string') {
      eventLogo = values.eventLogoOrImage;
    } else if (values.eventLogoOrImage instanceof File) {
      eventLogo = await getImage(values.eventLogoOrImage);
    }

    try {
      const data = getSaveCustomizationData(values, logo, eventLogo, eventId as number);

      const res = await dispatch(createEmailTemplate(data)).unwrap();

      if (res) {
        await dispatch(getEmailTemplate(eventId as number)).unwrap();
        toast.success('Saved successfully');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      await CommunicationService.sendCustomizationTemplate(eventId as number);
      toast.success('Test email sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (!eventId) return;
    const getCurrentTemplate = async () => {
      try {
        await dispatch(getEmailTemplate(eventId as number)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    getCurrentTemplate();
  }, [eventId]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({handleSubmit, setFieldValue, values, setValues}) => (
        <Form onSubmit={handleSubmit}>
          <Box sx={CustomizationStyle.mainWrapper}>
            <Box sx={CustomizationStyle.leftBoxWrapper}>
              <Box>
                <Typography sx={CustomizationStyle.heading}>Email Settings</Typography>

                <Box sx={CustomizationStyle.inputs}>
                  <Field id="fromName" name="fromName" label="Sender name" component={Input} placeholder="Enter name" />
                  <Field
                    id="replyTo"
                    name="replyTo"
                    label="Reply to"
                    component={Input}
                    placeholder="email@example.org"
                  />
                  <Field
                    id="primaryColor"
                    name="primaryColor"
                    label="Primary color"
                    component={ColorPicker}
                    placeholder="#FBFBFB"
                  />
                  <Field
                    id="bodyBackground"
                    name="bodyBackground"
                    label="Email background color"
                    component={ColorPicker}
                    placeholder="#FBFBFB"
                  />
                  <Field
                    id="headerColor"
                    name="headerColor"
                    label="Header background color"
                    component={ColorPicker}
                    placeholder="#FFFFFF"
                  />

                  <Box sx={{...formContentStyle.logoWrapper, minHeight: '100px'}}>
                    <Field
                      id="webpageLogo"
                      name="webpageLogo"
                      label="Upload Logo"
                      component={GreyUploadImage}
                      parentClassName={formContentStyle.logo}
                    />
                  </Box>

                  <Box sx={{display: 'flex'}}>
                    <Field id="logoHeight" name="logoHeight" label="Logo size" component={Input} placeholder="60px" />
                    <Box sx={{ml: '16px'}}>
                      <Typography sx={CustomizationStyle.logoPositionText}>Logo Position</Typography>
                      <Box sx={CustomizationStyle.flexGap}>
                        {['start', 'center', 'end'].map((pos) => {
                          const isSelected = values.logoPosition === pos;
                          return (
                            <Box
                              key={pos}
                              onClick={() => setFieldValue('logoPosition', pos)}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: isSelected ? '#26B5ED' : '#ddd',
                                backgroundColor: isSelected ? '#E6F7FD' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <Typography variant="body2">
                                {pos === 'end' ? <AlignLeft /> : pos === 'center' ? <AlignCenter /> : <AlignRight />}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{...formContentStyle.logoWrapper, minHeight: '100px', marginBottom: '28px'}}>
                    <Field
                      id="eventLogoOrImage"
                      name="eventLogoOrImage"
                      label="Event Logo or Image"
                      component={GreyUploadImage}
                      parentClassName={formContentStyle.logo}
                    />
                    <Typography
                      component="span"
                      display="block"
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: 'var(--neutral-300)',
                        marginTop: '-4px',
                      }}
                    >
                      Recommended size: 1200 × 600 px
                    </Typography>
                  </Box>

                  <Field
                    id="textColor"
                    name="textColor"
                    label="Text color"
                    component={ColorPicker}
                    placeholder="#FFFFFF"
                  />
                  <Field
                    id="optional1"
                    name="optional1"
                    component={Input}
                    label="Social links"
                    placeholder="https://www.facebook.com"
                    InputProps={{startAdornment: <Facebook />}}
                  />
                  <Field
                    id="optional2"
                    name="optional2"
                    component={Input}
                    placeholder="https://www.instagram.com"
                    InputProps={{startAdornment: <Instagram />}}
                  />
                  <Field
                    id="optional3"
                    name="optional3"
                    component={Input}
                    placeholder="https://www.twitter.com"
                    InputProps={{startAdornment: <Twitter />}}
                  />
                  <Field
                    id="optional4"
                    name="optional4"
                    component={Input}
                    placeholder="https://www.youtube.com"
                    InputProps={{startAdornment: <YouTube />}}
                  />
                </Box>

                <Box sx={CustomizationStyle.buttonsBlock}>
                  <Button variant="outline" title="Cancel" onClick={() => setValues(initialValues)} />

                  <Button title="Save changes" type="submit" disabled={isLoading} />
                </Box>
              </Box>
            </Box>

            <Box sx={CustomizationStyle.rightBoxWrapper}>
              <Box sx={{backgroundColor: '#FFFFFF'}}>
                <Typography sx={CustomizationStyle.rightHeading}>Template Preview</Typography>

                {communicationTemplateLoading ? (
                  <Loader sx={formContentStyle.loader} size={50} />
                ) : (
                  <Box>
                    <Box sx={CustomizationStyle.headingRight}>
                      <Box sx={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        <Typography sx={{color:'var(--neutral-300)'}}>From:</Typography> <span>{values.fromName}</span>
                      </Box>
                      <Box sx={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        <Typography sx={{color:'var(--neutral-300)'}}>Reply to:</Typography> <span>{values.replyTo}</span>
                      </Box>
                    </Box>
                    <Box sx={{p: '24px', backgroundColor: '#fbfbfb'}}>
                      <Box
                        sx={{
                          background: values.bodyBackground || '#FFFFFF',
                          height: 'calc(100vh - 404px)',
                          overflowY: 'auto',
                          borderRadius: '8px',
                        }}
                      >
                        <Box>
                          {values.webpageLogo ? (
                            <Box
                              sx={{
                                display: 'flex',
                                // justifyContent: position[values.logoPosition as keyof typeof position],
                                justifyContent: positionToFlex[values.logoPosition as keyof typeof positionToFlex],

                                padding: '12px',
                                background: values.headerColor,
                              }}
                            >
                              <img
                                src={
                                  isFile(values.webpageLogo)
                                    ? URL.createObjectURL(values.webpageLogo)
                                    : values.webpageLogo
                                }
                                alt="Uploaded Logo"
                                style={{
                                  maxWidth: '100%',
                                  marginBottom: '10px',
                                  height: `${values.logoHeight || communicationTemplate?.logoHeight}px`,
                                }}
                              />
                            </Box>
                          ) : (
                            <Box sx={{height: '68px', background: '#ffffff'}} />
                          )}
                        </Box>

                        <Box>
                          {values.eventLogoOrImage && (
                            <Box sx={{display: 'flex'}}>
                              <img
                                src={
                                  isFile(values.eventLogoOrImage)
                                    ? URL.createObjectURL(values.eventLogoOrImage)
                                    : values.eventLogoOrImage
                                }
                                alt="Event Logo"
                                style={CustomizationStyle.eventLogoRight}
                              />
                            </Box>
                          )}
                        </Box>

                        <Box sx={CustomizationStyle.emailContent}>
                          <Box>Email content</Box>
                        </Box>

                        <Box>
                          <Box sx={CustomizationStyle.linkCont}>
                            {[
                              {url: values.optional1, icon: <Facebook />},
                              {url: values.optional2, icon: <Instagram />},
                              {url: values.optional3, icon: <Twitter />},
                              {url: values.optional4, icon: <YouTube />},
                            ]
                              .filter((item) => Boolean(item.url))
                              .map((item, index) => (
                                <Box key={index} sx={CustomizationStyle.linkItem}>
                                  <a href={item.url} target="_blank" rel="noreferrer">
                                    {item.icon}
                                  </a>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={CustomizationStyle.buttonsBlock}>
                      <Button
                        title="Send test email"
                        onClick={sendTestEmail}
                        disabled={!communicationTemplate || isLoading}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default CommunicationCustomization;
